import { Helper } from "../common/helper";
import { Requester } from "../common/requester";
import { Txt } from "../common/text";
import { TimesheetShortcutsSet } from "./timesheet-shortcuts-set";
import { ConfirmationDialogHelper } from "../common/confirmation-dialog-helper";
import {
    TimesheetButtonRequest,
    TimesheetCellButton,
    TimesheetCellData,
    TimesheetChangedDates,
    TimesheetRightTableColumn,
    TimesheetRightTableRowData,
    TimesheetSaveData,
    TimesheetSaveRequest,
    TimesheetUpdateData,
    TimesheetUpdateRequest
} from "./timesheet-models";
import { TableHelper } from "../common/table-helper";
import { TimesheetSelectedCells } from "./timesheet-right-table-configurer";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { Icon, IconHelper } from "../common/icon-helper";
import { MessageDisplayer } from "../common/message-displayer";
import { Form, FormDisplayer } from "../forms/form";
import { Loader } from "../common/loader";

export class TimesheetTableData
{
    readonly generalUrlParameters: { [key: string]: string };
    readonly saveUrl: string;
    readonly dateDataUrl: string;
    private readonly rows: TimesheetRightTableRowData[] = [];

    constructor(generalUrlParameters: { [key: string]: string }, saveUrl: string, dateDataUrl: string)
    {
        this.generalUrlParameters = generalUrlParameters;
        this.saveUrl = saveUrl;
        this.dateDataUrl = dateDataUrl;
    }

    getRow(rowNumber: number): TimesheetRightTableRowData
    {
        return this.rows[rowNumber];
    }

    addRow(row: TimesheetRightTableRowData): void
    {
        this.rows.push(row);
    }

    getAllRows(): TimesheetRightTableRowData[]
    {
        return this.rows;
    }

    getCell(rowNumber: number, columnNumber: number): TimesheetCellData
    {
        return this.getRow(rowNumber).Cells[columnNumber];
    }
}

export class TimesheetChange
{
    readonly rowNumber: number;
    readonly columnNumber: number;
    readonly oldValue: string;
    readonly newValue: string;

    constructor(rowNumber: number, columnNumber: number, oldValue: string, newValue: string)
    {
        this.rowNumber = rowNumber;
        this.columnNumber = columnNumber;
        this.oldValue = oldValue;
        this.newValue = newValue;
    }
}

export class TimesheetRightTableDataManager
{
    private readonly columns: TimesheetRightTableColumn[];
    private readonly rows: string[];
    private readonly data: TimesheetTableData;
    private readonly shortcuts: TimesheetShortcutsSet;
    private readonly onChange: (updateData: TimesheetUpdateData) => void;

    constructor(columns: TimesheetRightTableColumn[], rows: string[], shortcuts: TimesheetShortcutsSet, data: TimesheetTableData,
                onChange: (updateData: TimesheetUpdateData) => void)
    {
        this.columns = columns;
        this.rows = rows;
        this.shortcuts = shortcuts;
        this.data = data;
        this.onChange = onChange;
    }

    getData()
    {
        return this.data;
    }

    save(changes: TimesheetChange[], restoreData: (change: TimesheetChange) => void)
    {
        const data: TimesheetChange[] = [];
        const saveData: TimesheetSaveData[] = [];
        const changedRows = new Set<number>();
        let smallestColumnId = this.columns.length;
        let largestColumnId = 0;
        let showWarning = false;
        for (const change of changes)
        {
            if (change.newValue === change.oldValue && this.data.getCell(change.rowNumber, change.columnNumber).ValueMatchesAllData)
            {
                continue;
            }
            if (change.oldValue && change.oldValue.startsWith(change.newValue + "^*"))
            {
                restoreData(change);
                continue;
            }
            const saveInfo = this.tryParseAndSetErrors(change);
            if (!saveInfo)
            {
                restoreData(change);
                continue;
            }
            data.push(change);
            saveData.push(saveInfo);
            changedRows.add(change.rowNumber);
            if (change.columnNumber < smallestColumnId)
            {
                smallestColumnId = change.columnNumber;
            }
            if (change.columnNumber > largestColumnId)
            {
                largestColumnId = change.columnNumber;
            }
            if (!showWarning && this.data.getCell(change.rowNumber, change.columnNumber).ShowWarningOnEdit)
            {
                showWarning = true;
            }
        }
        if (!data.length)
        {
            return;
        }

        const saveRequest: TimesheetSaveRequest = {
            saveInfo: saveData,
            updateInfo: {
                rows: this.getRowsToUpdateCore(Array.from(changedRows)),
                startDate: this.columns[smallestColumnId].Date,
                endDate: this.columns[largestColumnId].Date
            }
        };
        if (showWarning)
        {
            ConfirmationDialogHelper.askConfirmation(Txt.timesheetEditWarning,
                () => this.doSaveRequest(saveRequest),
                () =>
                {
                    for (const change of data)
                    {
                        this.data.getCell(change.rowNumber, change.columnNumber).Error = null;
                        restoreData(change);
                    }
                });
        }
        else
        {
            this.doSaveRequest(saveRequest);
        }
    }

    async reloadCellData(selectedCells: TimesheetSelectedCells, changedDates: TimesheetChangedDates, id?: string): Promise<void>
    {
        let startColumn = selectedCells.startColumn;
        if (changedDates?.EarliestChange)
        {
            const dif = Helper.getDifferenceInDays(this.columns[startColumn].Date, changedDates.EarliestChange);
            if (dif > 0)
            {
                startColumn -= dif;
                if (startColumn < 0)
                {
                    startColumn = 0;
                }
            }
        }
        let endColumn = selectedCells.endColumn;
        if (changedDates?.LatestChange)
        {
            const dif = Helper.getDifferenceInDays(changedDates.LatestChange, this.columns[endColumn].Date);
            if (dif > 0)
            {
                endColumn += dif;
                if (endColumn >= this.columns.length)
                {
                    endColumn = this.columns.length - 1;
                }
            }
        }
        const updateRequest: TimesheetUpdateRequest = {
            rows: this.getRowsToUpdate(selectedCells),
            startDate: this.columns[startColumn].Date,
            endDate: this.columns[endColumn].Date,
            editedItemId: id
        };
        const updateData = <TimesheetUpdateData> await Requester.post(this.data.dateDataUrl, updateRequest);
        this.onChange(updateData);
    }

    async doButtonAction(button: TimesheetCellButton, selectedCells: TimesheetSelectedCells)
    {
        if (button.OpenForm)
        {
            const form = new Form(button.Url);
            form.onDataChanged = id => this.reloadCellData(selectedCells, button.ItemPeriod, id);
            FormDisplayer.openInModal(form);
            return;
        }
        try
        {
            Loader.startGlobally();
            const ids: { [rowId: string]: { [date: string]: string[] } } = {};
            if (selectedCells.isSingleCell() && button.GroupButtonId)
            {
                ids[this.rows[selectedCells.startRow]] = {}
                ids[this.rows[selectedCells.startRow]][this.columns[selectedCells.startColumn].Date] = [button.GroupButtonId];
            }
            else
            {
                for (let r = selectedCells.startRow; r <= selectedCells.endRow; r++)
                {
                    ids[this.rows[r]] = {}
                    for (let c = selectedCells.startColumn; c <= selectedCells.endColumn; c++)
                    {
                        ids[this.rows[r]][this.columns[c].Date] = [];
                        for (const x of this.getButtons(r, c))
                        {
                            if (button.Url === x.Url && x.GroupButtonId)
                            {
                                ids[this.rows[r]][this.columns[c].Date].push(x.GroupButtonId);
                            }
                            for (const y of x.SubButtons)
                            {
                                if (button.Url === y.Url && y.GroupButtonId)
                                {
                                    ids[this.rows[r]][this.columns[c].Date].push(y.GroupButtonId);
                                }
                            }
                        }
                    }
                }
            }
            const request: TimesheetButtonRequest =
                    {
                        idsByRowAndDate: ids,
                        rowsToUpdate: this.getRowsToUpdate(selectedCells)
                    };
            let updateData: TimesheetUpdateData;
            try
            {
                updateData = await Requester.post(Requester.getUrl(button.Url, this.data.generalUrlParameters), request);
            }
            catch (error)
            {
                MessageDisplayer.showErrorFromServer(error);
            }
            if (updateData)
            {
                this.onChange(updateData);
            }
        }
        finally
        {
            Loader.stopGlobally();
        }
    }

    getRowsToUpdate(selectedCells: TimesheetSelectedCells): string[]
    {
        const changedRows: number[] = [];
        for (let rowNumber = selectedCells.startRow; rowNumber <= selectedCells.endRow; rowNumber++)
        {
            changedRows.push(rowNumber);
        }
        return this.getRowsToUpdateCore(changedRows);
    }

    private getRowsToUpdateCore(changedRows: number[]): string[]
    {
        const changedEmployees = new Set();
        for (const rowNumber of changedRows)
        {
            changedEmployees.add(this.data.getRow(rowNumber).EmployeeId);
        }
        const rowsToUpdate: string[] = [];
        for (let i = 0; i < this.rows.length; i++)
        {
            if (changedEmployees.has(this.data.getRow(i).EmployeeId))
            {
                rowsToUpdate.push(this.rows[i]);
            }
        }
        return rowsToUpdate;
    }

    getColumnHeader(columnNumber): string
    {
        const column = this.columns[columnNumber];
        return TableHelper.getDateCellHtml(column.Date, column.Color);
    }

    getRowHeader(rowNumber: number, commentsManager: TimesheetCommentsManager): string
    {
        const rowData = this.data.getRow(rowNumber);
        const comment = commentsManager.getComment(rowNumber, null);
        return Helper.isMobileScreen() ? rowData.FullName :
            comment ? `${rowData.ShortTitle} (${comment})` : rowData.ShortTitle;
    }

    getDisplayableData(): string[][]
    {
        const result: string[][] = [];
        for (const row of this.data.getAllRows())
        {
            const rowData: string[] = [];
            for (const cell of row.Cells)
            {
                rowData.push(cell.Value);
            }
            result.push(rowData);
        }
        return result;
    }

    setCellData(rowNumber: number, columnNumber: number, value: TimesheetCellData)
    {
        this.data.getRow(rowNumber).Cells[columnNumber] = value;
    }

    generateTriangleBorderSVG(color)
    {
        const triangleHeight = 4;
        const triangleBase = 8;
        const spacing = 7;
        const step = triangleBase + spacing;

        const topSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${step}" height="${triangleHeight}">
            <polygon points="0,0 ${triangleBase},0 ${triangleBase / 2},${triangleHeight}" fill="${color}"/>
        </svg>
    `)}`;

        const bottomSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${step}" height="${triangleHeight}">
            <polygon points="${triangleBase / 2},0 0,${triangleHeight} ${triangleBase},${triangleHeight}" fill="${color}"/>
        </svg>
    `)}`;

        const leftSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${triangleHeight}" height="${step}">
            <polygon points="0,0 ${triangleHeight},${triangleBase / 2} 0,${triangleBase}" fill="${color}"/>
        </svg>
    `)}`;

        const rightSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${triangleHeight}" height="${step}">
            <polygon points="0,${triangleBase / 2} ${triangleHeight},0 ${triangleHeight},${triangleBase}" fill="${color}"/>
        </svg>
    `)}`;

        return { topSVG, bottomSVG, leftSVG, rightSVG, triangleHeight, triangleBase, step };
    }

    formatCell(rowNumber, columnNumber, cell, value)
    {
        const row = this.data.getRow(rowNumber);
        if (row.HideBottomBorder)
        {
            cell.css("border-bottom", "0");
        }
        const cellData = this.data.getCell(rowNumber, columnNumber);
        if (value === cellData.Value)
        {
            let html = cellData.DisplayValue;
            if (!cellData.IsEditable)
            {
                html = "<i>" + html + "</i>";
            }
            if (cellData.Error)
            {
                cell.css("position", "relative");
                html += "<i style=\"color:red; top:0; right:0; position:absolute\" class=\"" + IconHelper.getIconClass(Icon.Error) + "\" /i>";
            }
            cell.html(html);
            cell.css("white-space", "nowrap");
            Helper.setColors(cell, cellData.Colors ?? [row.DefaultColor]);

            if (cellData.BorderColor)
            {
                const { topSVG, bottomSVG, leftSVG, rightSVG, triangleHeight, step } =
                    this.generateTriangleBorderSVG(cellData.BorderColor);

                cell.addClass("timesheet-cell-with-border");

                cell.get(0).style.setProperty(
                    "--triangle-bg",
                    `url("${topSVG}"), url("${bottomSVG}"), url("${leftSVG}"), url("${rightSVG}")`
                );

                cell.get(0).style.setProperty(
                    "--triangle-size",
                    `${step}px ${triangleHeight}px, ${step}px ${triangleHeight}px, ${triangleHeight}px ${step}px, ${triangleHeight}px ${step}px`
                );
            }
            else
            {
                cell.removeClass("timesheet-cell-with-border");
                cell.get(0).style.removeProperty("--triangle-bg");
                cell.get(0).style.removeProperty("--triangle-size");
            }
        }
        else if (value && value.indexOf("^*") !== -1)
        {
            cell.text(value.substring(0, value.lastIndexOf("^*")));
        }
        cell.tooltip({
            title: () =>
            {
                const cellInfo = this.data.getCell(rowNumber, columnNumber);
                return cellInfo.Error ?? cellInfo.Tooltip;
            },
            container: "body",
            placement: "top"
        });
    }

    isCellEditable(rowNumber: number, columnNumber: number): boolean
    {
        return this.data.getCell(rowNumber, columnNumber).IsEditable;
    }

    getButtons(rowNumber: number, columnNumber: number): TimesheetCellButton[]
    {
        return this.data.getCell(rowNumber, columnNumber).Buttons.concat(this.data.getRow(rowNumber).Buttons.map(x =>
            x.OpenForm ? { ...x, Url: Requester.getUrl(x.Url, { date: this.columns[columnNumber].Date }) } : x));
    }

    getShortcuts(): TimesheetShortcutsSet
    {
        return this.shortcuts;
    }

    private async doSaveRequest(saveRequest: TimesheetSaveRequest)
    {
        let updateData: TimesheetUpdateData;
        try
        {
            updateData = await Requester.post(this.data.saveUrl, saveRequest);
        }
        catch (err)
        {
            MessageDisplayer.showErrorFromServer(err);
        }
        this.onChange(updateData);
    }

    private tryParseAndSetErrors(change: TimesheetChange): TimesheetSaveData
    {
        let value = change.newValue ?? "";
        let extraSaveInfo = null;
        if (value.indexOf("^*") !== -1)
        {
            extraSaveInfo = value.substring(value.lastIndexOf("^*") + 2);
            value = value.substring(0, value.lastIndexOf("^*"));
        }
        let shortcut: string = null;
        let hours: number = null;
        let startTime: string = null;
        let endTime: string = null;
        const row = this.data.getRow(change.rowNumber);
        const shortcutItem = this.shortcuts.tryGetByAbbreviation(value);
        if (shortcutItem)
        {
            if (!row.AllowedShortcutTypes.includes(shortcutItem.Type))
            {
                row.Cells[change.columnNumber].Error = Txt.cannotInsertThisShortcutToThisRowInTimesheet;
                return null;
            }
            shortcut = shortcutItem.Id;
        }
        else if (Helper.isFloat(value) && Helper.parseFloat(value) <= 24)
        {
            if (!row.AllowHours)
            {
                row.Cells[change.columnNumber].Error = Txt.cannotInsertWorkToThisRowInTimesheet;
                return null;
            }
            hours = Helper.parseFloat(value);
        }
        else if (value.indexOf("-") !== -1)
        {
            const parts = value.split("-");
            const tryParseTimeNumber = (timeToParse: string, parseMinutes: boolean = false): string =>
            {
                if (timeToParse.length === 1 && !parseMinutes || timeToParse.length === 2)
                {
                    const parsedNumber = parseInt(timeToParse);
                    if (parsedNumber < (parseMinutes ? 60 : 24) && parsedNumber >= 0)
                    {
                        return parsedNumber < 10 ? "0" + parsedNumber.toString() : parsedNumber.toString();
                    }
                }
                return null;
            };
            const tryParseTime = (timeToParse: string): string =>
            {
                if (timeToParse.length === 1 || timeToParse.length === 2)
                {
                    const parsed = tryParseTimeNumber(timeToParse);
                    return parsed === null ? null : parsed + ":00";
                }
                const timeParts = timeToParse.split(":");
                if (timeParts.length === 2)
                {
                    const hoursPart = tryParseTimeNumber(timeParts[0]);
                    const minutesPart = tryParseTimeNumber(timeParts[1], true);
                    return hoursPart === null || minutesPart === null ? null : hoursPart + ":" + minutesPart;
                }
                return null;
            };
            if (parts.length === 2)
            {
                startTime = tryParseTime(parts[0]);
                endTime = tryParseTime(parts[1]);
                if (startTime === null || endTime === null)
                {
                    row.Cells[change.columnNumber].Error = Txt.invalidValueInsertedInTimesheet;
                    return null;
                }
            }
        }
        else
        {
            if (value !== "")
            {
                row.Cells[change.columnNumber].Error = Txt.invalidValueInsertedInTimesheet;
                return null;
            }
        }
        return {
            date: this.columns[change.columnNumber].Date,
            row: this.rows[change.rowNumber],
            shortcut: shortcut,
            hours: hours,
            startTime: startTime,
            endTime: endTime,
            extraSaveInfo: extraSaveInfo
        };
    }
}
